import { SelectionMode } from '@uifabric/utilities'
import { EntityList, renderDate } from 'components/EntityList'
import { FieldProps } from 'formik'
import { Stack } from 'forms/FormStack'
import { Box } from 'grid'
import { useCurrentFormik } from 'hooks/useCurrentFormikContext'
import { useCurrentProving } from 'hooks/useCurrentProving'
import useSelection from 'hooks/useSelection'
import { Proving, ResourceBooking } from 'model'
import {
  ConstrainMode,
  DefaultButton,
  IStackTokens,
  PrimaryButton,
} from 'office-ui-fabric-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { createFieldColumn } from 'views/helpers'
import { useDeleteResourceBookingMutation } from 'views/ProvingDetailsView/hooks'

export const ResourceSearchOccupiedResourcesList: React.FC<{
  fieldProps: FieldProps<any>
  allResourceBookings: any
}> = ({ fieldProps, allResourceBookings }) => {
  const { t } = useTranslation()
  const fieldColumn = React.useMemo(() => createFieldColumn(t), [t])
  const { form, field } = fieldProps
  const [deleteResourceBooking] = useDeleteResourceBookingMutation()
  const [proving, refetch] = useCurrentProving()
  const formikContext = useCurrentFormik()
  const initialValues = formikContext ? formikContext.initialValues : undefined
  const resourceType = initialValues.resourceRequirementType
    ? initialValues.resourceRequirementType
    : ''
  const [selection] = useSelection<any>({
    onSelectionChanged(selectedItems) {
      form.setFieldValue(field.name, selectedItems)
    },
    getKey: (item) => item._id,
  })

  const resourceBookings = () => {
    const occupiedRessourcesItems: any[] = []

    if (
      allResourceBookings &&
      allResourceBookings.data &&
      allResourceBookings.data.provings
    ) {
      allResourceBookings.data.provings.forEach((p: Proving) => {
        if (p.provingGround!._id === proving.provingGround!._id) {
          p.resourceBookings!.forEach((rb) => {
            if (
              (rb.resource && rb.resource.type === resourceType) ||
              (rb.resource && rb.resource.category === resourceType)
            ) {
              if (
                rb.dateFrom < initialValues.dateTo &&
                initialValues.dateFrom < rb.dateTo
              ) {
                ;(rb as any).provingId = p._id
                ;(rb as any).erpnr = p.id
                occupiedRessourcesItems.push(rb)
              }
            }
          })
        }
      })
    }
    return occupiedRessourcesItems
  }
  let occupiedRessources = resourceBookings()

  const handleConfirmDelete = async () => {
    const ids: any[] = []

    form.values.occupiedResources.forEach(async (rr: any) => {
      ids.push(rr._id)
    })
    const { errors } = await deleteResourceBooking({
      variables: {
        provingId: form.values.occupiedResources[0].provingId,
        idsToDelete: ids,
      },
    })
    if (errors) {
      throw errors
    }
    selection.setAllSelected(false)
    occupiedRessources = resourceBookings()
    refetch()
  }

  const columnsOccupiedRessources = [
    fieldColumn('parent.title', {
      onRender: (item: ResourceBooking, i, col) => {
        const value = item.resource.parent ? item.resource.parent!.title : ''
        return (
          <Box key={i} style={{ whiteSpace: 'pre-line' }}>
            {value}
          </Box>
        )
      },
      minWidth: 100,
      ungroupable: true,
    }),
    fieldColumn('resource', {
      onRender: (item: ResourceBooking, i, col) => {
        return (
          <Box key={i} style={{ whiteSpace: 'pre-line' }}>
            {item.resource.title}
          </Box>
        )
      },
      ungroupable: true,
      minWidth: 100,
    }),
    fieldColumn('dateFrom', { ungroupable: true, onRender: renderDate }),
    fieldColumn('dateTo', { ungroupable: true, onRender: renderDate }),
    fieldColumn('erpnr', {
      minWidth: 50,
      ungroupable: true,
    }),
  ]
  const stackTokens: IStackTokens = {
    childrenGap: 5,
  }

  const openResourceBooking = () => {
    window.open(
      window.location.origin +
        `/provings/${form.values.occupiedResources[0].erpnr}` +
        '?page=resourceBookings'
    )
  }

  return (
    <>
      <EntityList
        items={occupiedRessources}
        selection={selection}
        columns={columnsOccupiedRessources}
        alternate={true}
        stickyHeader={true}
        selectionMode={SelectionMode.single}
        constrainMode={ConstrainMode.horizontalConstrained}
        onItemInvoked={() => {
          localStorage.setItem(
            'resourceBooking',
            JSON.stringify(form.values.occupiedResources[0])
          )
          openResourceBooking()
        }}
      />
      <Stack
        horizontal={true}
        horizontalAlign="end"
        tokens={stackTokens}
        styles={{ root: { paddingTop: 10 } }}
      >
        <DefaultButton
          disabled={selection.count !== 1}
          onClick={() => {
            localStorage.setItem(
              'resourceBooking',
              JSON.stringify(form.values.occupiedResources[0])
            )
            openResourceBooking()
          }}
        >
          {t('field.openResourceBooking.label')}
        </DefaultButton>
        <PrimaryButton
          onClick={() => {
            handleConfirmDelete()
          }}
          disabled={selection.count !== 1}
        >
          {t('field.removeResourceBooking.label')}
        </PrimaryButton>
      </Stack>
    </>
  )
}
